import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';
import type { AtAGlanceOverviewItem } from '../../at-a-glance/atAGlanceTypes.ts';
import { ConnectionsByLineSectionShell } from '../connections-by-line-section/ConnectionsByLineSectionShell.tsx';
import { TeamworkGraphAskRovoButton } from '../TeamworkGraphAskRovoButton.tsx';

const LOADING_BAR_WIDTHS = ['100%', '85%', '70%'] as const;

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
		marginBlock: token('space.0'),
		marginInline: token('space.0'),
		paddingInlineStart: token('space.250'),
	},
	// Same column + gap as the bullet list so the body height stays close when
	// the overview replaces the placeholder bars.
	loading: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	// The shell's body grid only gives `space.050` between rows; the action wants a clear
	// break from the prose above it.
	askRovo: {
		paddingBlockStart: token('space.100'),
	},
});

const i18n = defineMessages({
	atAGlance: {
		id: 'byline-connections.preview-panel.section.at-a-glance',
		defaultMessage: 'At a glance',
		description:
			'Heading of the Teamwork Graph section that summarises this page and its connections in a few bullets. The section is open by default.',
	},
	loading: {
		id: 'byline-connections.preview-panel.section.at-a-glance.loading',
		defaultMessage: 'Loading at a glance',
		description:
			'Accessible label for the placeholder bars shown in the At a glance section while overview bullets are being generated.',
	},
	error: {
		id: 'byline-connections.preview-panel.section.at-a-glance.error',
		defaultMessage: "We couldn't generate this summary",
		description:
			'Shown in the At a glance section when generating the overview bullets fails. Tells the user the summary is unavailable without exposing the technical error.',
	},
	insufficientEvidence: {
		id: 'byline-connections.preview-panel.section.at-a-glance.insufficient-evidence.ai-non-final',
		defaultMessage:
			'We couldn’t generate summaries because there isn’t enough information in this page and its connections.',
		description:
			'Shown in the At a glance section when the summaries service succeeds but the page and its connections do not contain enough information to generate useful summaries.',
	},
});

export type AtAGlanceSectionProps = UseAtAGlanceResult & {
	/**
	 * Opens Rovo chat. When passed, the section ends with the "Chat with Rovo" button; when
	 * omitted (no Rovo on the site), no button renders.
	 */
	onAskRovo?: () => void;
};

const AtAGlanceBullet = ({ item }: { item: AtAGlanceOverviewItem }): React.JSX.Element => {
	return (
		<Box as="li">
			<Text as="span">{item.text}</Text>
		</Box>
	);
};

/**
 * The "At a glance" section: the overview bullets of the cc-smarts response
 * `ConnectionSections` is handed. The card chrome renders as soon as the request
 * is in flight, with shimmering text-line placeholders in the body; an error
 * keeps the chrome and shows a failure message. Insufficient evidence keeps the
 * chrome with a neutral explanation; unavailable or generated-without-overview
 * hides the section. Starts expanded, with no count and no icon stack: the body
 * is prose, not a list of connections.
 *
 * The per-link one-liners of the same response belong to Related content rows,
 * not here.
 *
 * The "Chat with Rovo" button lives at the end of this body on both surfaces, in every
 * state the section shows — the button does not depend on the bullets, and the section is
 * the panel's home for Rovo now that the footer carries Share context alone.
 */
export const AtAGlanceSection = ({
	output,
	loading,
	error,
	onAskRovo,
}: AtAGlanceSectionProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const overview = output?.overview;

	if (
		!loading &&
		!error &&
		(!output ||
			output.status === 'UNAVAILABLE' ||
			(output.status === 'GENERATED' && !overview?.length))
	) {
		return null;
	}

	return (
		<Box xcss={styles.card}>
			<ConnectionsByLineSectionShell
				title={formatMessage(i18n.atAGlance)}
				iconBefore={<AiSparkleIcon label="" spacing="none" />}
				defaultExpanded
				testId="teamwork-graph-preview-panel-at-a-glance-section"
			>
				{loading ? (
					<Box
						xcss={styles.loading}
						role="status"
						aria-label={formatMessage(i18n.loading)}
						testId="teamwork-graph-preview-panel-at-a-glance-section--loading"
					>
						{LOADING_BAR_WIDTHS.map((width) => (
							<Skeleton
								key={width}
								width={width}
								height={token('space.200')}
								borderRadius={token('radius.small')}
								isShimmering
							/>
						))}
					</Box>
				) : overview?.length ? (
					// Keep bullets if a later refetch fails (related-content hydrate, show more).
					<Box as="ul" xcss={styles.list}>
						{overview.map((item, index) => (
							<AtAGlanceBullet key={`${item.label}-${item.text}-${index}`} item={item} />
						))}
					</Box>
				) : error ? (
					<Box role="alert" testId="teamwork-graph-preview-panel-at-a-glance-section--error">
						<Text as="p" color="color.text.subtle">
							{formatMessage(i18n.error)}
						</Text>
					</Box>
				) : output?.status === 'INSUFFICIENT_EVIDENCE' ? (
					<Box testId="teamwork-graph-preview-panel-at-a-glance-section--insufficient-evidence">
						<Text as="p" color="color.text.subtle">
							{formatMessage(i18n.insufficientEvidence)}
						</Text>
					</Box>
				) : null}
				{onAskRovo ? (
					<Box
						xcss={styles.askRovo}
						testId="teamwork-graph-preview-panel-at-a-glance-section--ask-rovo-slot"
					>
						<TeamworkGraphAskRovoButton
							onClick={onAskRovo}
							testId="teamwork-graph-preview-panel-at-a-glance-section--ask-rovo"
						/>
					</Box>
				) : null}
			</ConnectionsByLineSectionShell>
		</Box>
	);
};
